import {Accordion, Button, CloseButton, Form, Modal} from "react-bootstrap";
import React, {useCallback, useEffect, useState} from "react";
import {filterSetAll} from '../../redux/slices/scheduleFilters.slice';
import {useDispatch, useSelector} from "react-redux";
import {getLengthOptions, naturalCompare} from "../../utilities";
import {selectAll as selectAllStyles} from "../../redux/slices/styles.slice";
import {selectAll as selectAllTeachers} from "../../redux/slices/teachers.slice";
import {selectAll as selectAllLevels} from "../../redux/slices/levels.slice";
import { timeSlotInterval } from "constants/variables";

const DialogModalMemo = ({onClose, isOpen}) => {
    const allStyles = useSelector(selectAllStyles);
    const allTeachers = useSelector(selectAllTeachers);
    const allLevels = useSelector(selectAllLevels);
    const storedFilters = useSelector((state) => state.scheduleFilters.filters)
    const [filters, setFilters] = useState({});
    
    const dispatch = useDispatch()
    const onSubmit = () => {
        let activeFilters = {}
        for (let [key, value] of Object.entries(filters)) {
            activeFilters[key] = value.filter((item) => item.checked === true)
        }

        const activeFiltersKeys = Object.keys(activeFilters).filter(item => activeFilters[item].length > 0)
        if(activeFiltersKeys.length === 0) {
            dispatch(filterSetAll(null))
        }
        else {
            dispatch(filterSetAll(filters))
        }
        onClose(false)
    }
    
    const defaultOptions = useCallback(() => {
        const getOptions = (data, entity) => {
            return data
                .map((item) => ({ 
                    value: item.color || item[`${entity}Name`], 
                    label: item[`${entity}Name`], 
                    entity: `${entity}s`, 
                    id: item.id,
                    checked: false
                }))
                .sort((a, b) => naturalCompare(a.label, b.label))
        }
        const [lengthOptions] = getLengthOptions([], timeSlotInterval.minutes_5);
        const optionsStyles = getOptions(allStyles, 'style');
        const optionsTeachers = getOptions(allTeachers, 'teacher');
        const optionsLevels = getOptions(allLevels, 'level');

        const filters = {
            length: lengthOptions.map(opt => ({ ...opt, checked: false })),
            levels: optionsLevels,
            styles: optionsStyles,
            teachers: optionsTeachers
        }
        return filters;
    }, [allLevels, allStyles, allTeachers])

    const resetFilters = () => {
        const resetFilterState = defaultOptions();
        setFilters(resetFilterState);
        dispatch(filterSetAll(null));
        onClose(false);
    }

    useEffect(() => {
        if(!storedFilters){
            const resetFilterState = defaultOptions();
            setFilters(resetFilterState);
        } else {
            const sanitizedFilters = { ...storedFilters };
            Object.keys(sanitizedFilters).forEach(key => {
                if (Array.isArray(sanitizedFilters[key])) {
                    sanitizedFilters[key] = sanitizedFilters[key].map(item => ({
                        ...item,
                        checked: item.checked || false
                    }));
                }
            });
            setFilters(sanitizedFilters);
        }
    }, [defaultOptions, storedFilters]);

    const onSelectChange = (field) => (event, index) => {
        const {checked} = event.currentTarget
        const newFiltersArr = JSON.parse(JSON.stringify(filters))
        newFiltersArr[field][index].checked = checked
        setFilters(newFiltersArr)
    }

    if (!isOpen) {
        return null
    }

    return (
        <div className="filtersModal">
            <div className="modal-header justify-content-center">
                <h5 className="modal-title">Filters</h5>
                <CloseButton onClick={onClose}/>
            </div>

            <div className="modal-body">
                <Accordion className="" flush>
                    <Accordion.Item eventKey="0">
                        <Accordion.Header><p className="m-0">Level</p></Accordion.Header>
                        <Accordion.Body className="accordionBodyFilter">
                            <div className="d-flex flex-column mb-2">
                                {filters?.levels?.map((item, index) => {
                                    return (
                                        <Form.Check
                                            checked={item?.checked || false}
                                            onChange={(event) => onSelectChange("levels")(event, index)}
                                            bsPrefix="checkboxFilters"
                                            inline
                                            value={item.value}
                                            name={item.label}
                                            label={item.label}
                                            type="checkbox"
                                            id={`level-checkbox-${index}`}
                                            key={`level-checkbox-${index}`}

                                        />
                                    )
                                })}
                            </div>
                        </Accordion.Body>
                    </Accordion.Item>
                    <Accordion.Item eventKey="1">
                        <Accordion.Header><p className="m-0">Style</p></Accordion.Header>
                        <Accordion.Body className="accordionBodyFilter">
                            <div className="d-flex flex-column mb-2">
                                {filters.styles?.map((item, index) => {
                                    return (
                                        <Form.Check
                                            checked={item?.checked || false}
                                            value={item.value}
                                            bsPrefix="checkboxFilters"
                                            inline
                                            onChange={(event) => onSelectChange("styles")(event, index)}
                                            label={item.label}
                                            name={item.label}
                                            type="checkbox"
                                            id={`styles-checkbox-${index}`}
                                            key={`styles-checkbox-${index}`}
                                        />
                                    )
                                })}
                            </div>
                        </Accordion.Body>
                    </Accordion.Item>
                    <Accordion.Item eventKey="2">
                        <Accordion.Header><p className="m-0">Teacher</p></Accordion.Header>
                        <Accordion.Body className="accordionBodyFilter">
                            <div className="d-flex flex-column mb-2">
                                {filters.teachers?.map((item, index) => {
                                    return (
                                        <Form.Check
                                            checked={item?.checked || false}
                                            value={item.value}
                                            onChange={(event) => onSelectChange("teachers")(event, index)}
                                            bsPrefix="checkboxFilters"
                                            inline
                                            label={item.label}
                                            name={item.label}
                                            type="checkbox"
                                            id={`teacher-checkbox-${index}`}
                                            key={`teacher-checkbox-${index}`}
                                        />
                                    )
                                })}
                            </div>
                        </Accordion.Body>
                    </Accordion.Item>
                    <Accordion.Item eventKey="3">
                        <Accordion.Header><p className="m-0">Length</p></Accordion.Header>
                        <Accordion.Body className="accordionBodyFilter">
                            <div className="d-flex flex-column mb-2">
                                {filters.length?.map((item, index) => {
                                    return (
                                        <Form.Check
                                            checked={item?.checked || false}
                                            onChange={(event) => onSelectChange("length")(event, index)}
                                            bsPrefix="checkboxFilters"
                                            inline
                                            value={item.value}
                                            label={item.label}
                                            name={item.label}
                                            type="checkbox"
                                            id={`length-checkbox-${index}`}
                                            key={`length-checkbox-${index}`}
                                        />
                                    )
                                })}
                            </div>
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>
            </div>
            <div className="modal-footer justify-content-center">
                <Button onClick={resetFilters} className="greyButton mediumSizeButton" variant="outline-secondary">Reset
                    All</Button>
                <Button onClick={onSubmit} className="primaryButton mediumSizeButton"
                        variant="primary">Submit</Button>
            </div>
        </div>
    );
}
export const DialogModal = React.memo(DialogModalMemo);